import React from "react";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";

// Uploaded-document values come back as absolute URLs built from the backend's FILE_URL,
// so the host differs per environment. Derive the prefix from the configured API URL
// instead of hard-coding a host.
const UPLOADS_BASE = `${(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "")}/uploads`;

const InsuranceDetails = ({ data }) => {
  const { insurance_type } = data?.order_details || {};
  const { insurance_plan, insurer_name } = data?.order_data || {};

  const insuranceTypeMapping = {
    1: "Foreign Worker",
    2: "Term Life",
    3: "Health Insurance",
    4: "Personal Accident",
    5: "Motor",
    6: "Offshore Insurance",
    7: "Group Insurance",
    8: "Property Insurance",
  };

  const getInsuranceData = () => {
    switch (insurance_type) {
      case 1:
        return data.foreign_worker_insurance;
      case 2:
        return data.term_life_insurance;
      case 3:
        return data.health_insurance;
      case 4:
        return data.pa_insurance;
      case 5:
        return data.motor_insurance;
      case 6:
        return data.offshore_insurance;
      case 7:
        return data.group_insurance;
      case 8:
        return data.property_insurance;
      default:
        return {};
    }
  };

  const insuranceData = getInsuranceData();

  const renderValue = (value) => {
    if (Array.isArray(value) && value.length === 0) {
      return <strong>N/A</strong>;
    }

    if (typeof value === "object" && value !== null) {
      return (
        <ul>
          {Object.entries(value).map(([key, val]) => (
            <li key={key}>
              <strong>{key.replace(/_/g, " ")}:</strong> {renderValue(val)}
            </li>
          ))}
        </ul>
      );
    }

    if (
      value &&
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}/.test(value) &&
      !isNaN(Date.parse(value))
    ) {
      return formatDateTime(value);
    }

    if (
      typeof value === "string" &&
      value.startsWith(UPLOADS_BASE)
    ) {
      return (
        <a href={`${value}`} target="_blank" rel="noopener noreferrer">
          {value.split("/").pop() || "N/A"}
        </a>
      );
    }

    return <strong>{value ? value.toString() : "N/A"}</strong>;
  };

  const excludedFields = [
    "user_id",
    "enquirer_name",
    "full_name",
    "id_type",
    "id_no",
    "email",
    "phone_number",
    "dob",
    "gender",
    "extraCoverages",
  ];

  const renderExtraCoverages = (extraCoverages) => {
    if (!extraCoverages || extraCoverages.length === 0) {
      return (
        <div className="col-md-12 mb-3">
          <label className="form-label">Extra Coverages:</label>
          <p>
            <b>N/A</b>
          </p>
        </div>
      );
    }

    return (
      <div className="col-md-12 mb-3">
        <label className="form-label">Extra Coverages:</label>
        {extraCoverages.map((coverage, index) => (
          <div key={index} className="mb-2">
            <strong>{coverage.description || "N/A"}</strong>
            <ul>
              <li>
                <strong>Sum Insured:</strong> {coverage.sum_insured || "N/A"}
              </li>
              <li>
                <strong>Amount:</strong> {coverage.amount || "N/A"}
              </li>
              <li>
                <strong>Cart Amount:</strong> {coverage.cart_amount || "N/A"}
              </li>
              <li>
                <strong>Cart Days:</strong> {coverage.cart_days || "N/A"}
              </li>
              <li>
                <strong>Created At:</strong>{" "}
                {coverage.created_at
                  ? new Date(coverage.created_at).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                    })
                  : "N/A"}
              </li>
            </ul>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="row">
      <div className="col-md-4 mb-3">
        <label className="form-label">Insurance Plan Type:</label>
        <p>
          <b>{insuranceTypeMapping[insurance_type] || "N/A"}</b>
        </p>
      </div>
      <div className="col-md-4 mb-3">
        <label className="form-label">Insurance Provider/Company:</label>
        <p>
          <b>{insurer_name || "N/A"}</b>
        </p>
      </div>
      <div className="col-md-4 mb-3">
        <label className="form-label">Room & Board Details:</label>
        <p>
          <b>{insurance_plan || "N/A"}</b>
        </p>
      </div>
      {insuranceData &&
        Object.keys(insuranceData)
          .filter((key) => !excludedFields.includes(key))
          .map((key) => (
            <div className="col-md-4 mb-3" key={key}>
              <label className="form-label">{key.replace(/_/g, " ")}:</label>
              <p>
                <b>
                  {insuranceData[key] === 1
                    ? "true"
                    : insuranceData[key] === 0
                    ? "false"
                    : renderValue(insuranceData[key])}
                </b>
              </p>
            </div>
          ))}
      {insuranceData &&
        insuranceData.extraCoverages &&
        renderExtraCoverages(insuranceData.extraCoverages)}
    </div>
  );
};

export default InsuranceDetails;
